import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/data/site";

const CHAPTERS = [
  {
    num: "01",
    title: "The Machine",
    quote: "A factory overlander, built a decade before the word existed.",
    body: "The Syncro was Volkswagen's quiet masterpiece: full-time four-wheel drive through a viscous coupling, a rear differential lock, and a first gear so low it crawls where others spin. This 1987 tin top keeps the solid steel roof collectors hunt for — no pop-top canvas, no leaks, just clean lines and Wolfsburg weld work. Under the hatch lives the 2.1-litre Waterboxer flat-four, torquey, honest, and mechanically sorted.",
    image: IMAGES.moab,
    position: "25% 55%",
    alt: "Syncro at the Poison Spider Trailhead, Moab red rock country",
    caption: "FIG. 01 — BUILT FOR THE LONG WAY ROUND",
  },
  {
    num: "02",
    title: "The Build",
    quote: "Kept honest. Upgraded where it counts.",
    body: "Years of considered upgrades make this more than a survivor. The suspension is lifted and refreshed, the rubber is all-terrain, and the South African headlight upgrade lights the trail ahead — brilliantly. Inside, the cabin has been sympathetically renewed — original character intact, comfort improved. It drives like a dream, pulls strong through every gear, and is sold turn-key with records.",
    image: IMAGES.lift,
    alt: "Syncro up on the shop lift during maintenance",
    caption: "FIG. 02 — LOVINGLY MAINTAINED",
  },
  {
    num: "03",
    title: "The Expedition Kit",
    quote: "Sleep three above the treeline. Carry everything below it.",
    body: "The van is offered with its optional expedition hardware: heavy-duty roof racks rated for real loads, Yakima Mega-Warrior basket, a custom-fabricated hatch-mounted bike rack, and a rooftop tent that unfolds into a triple berth with a view. Bolt them on as needed for the season, or strip it back to a clean tin top for day to day — both lives suit it.",
    image: IMAGES.hero,
    position: "50% 45%",
    aspect: "aspect-[3/2]",
    alt: "Syncro with full expedition kit before a mountain backdrop",
    caption: "FIG. 03 — FULL KIT, READY",
  },
];

function Chapter({ chapter, flip }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <div
      ref={ref}
      className={`relative grid md:grid-cols-12 gap-10 md:gap-16 items-center py-20 md:py-32 border-t border-border`}
      data-testid={`chapter-${chapter.num}`}
    >
      <span
        aria-hidden
        className={`absolute -top-6 md:-top-14 ${flip ? "right-0" : "left-0"} font-headings text-[34vw] md:text-[18vw] leading-none text-accent/40 select-none pointer-events-none`}
      >
        {chapter.num}
      </span>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className={`relative md:col-span-6 ${flip ? "md:order-2" : ""}`}
      >
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-4">Chapter {chapter.num}</p>
        <h2 className="font-headings uppercase text-4xl md:text-6xl leading-[0.9] mb-6">{chapter.title}</h2>
        <p className="font-editorial italic text-2xl md:text-3xl text-secondary leading-snug mb-6">“{chapter.quote}”</p>
        <p className="text-base md:text-lg leading-relaxed text-foreground/80 max-w-xl">{chapter.body}</p>
      </motion.div>

      <motion.figure
        initial={{ opacity: 0, scale: 0.96 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
        className={`relative md:col-span-6 ${flip ? "md:order-1" : ""}`}
      >
        <div className="overflow-hidden" style={{ clipPath: flip ? "polygon(0 0, 100% 4%, 96% 100%, 0 96%)" : "polygon(4% 0, 100% 0, 100% 96%, 0 100%)" }}>
          <motion.img
            style={{ y, objectPosition: chapter.position || "50% 50%" }}
            src={chapter.image}
            alt={chapter.alt}
            loading="lazy"
            className={`w-full ${chapter.aspect || "aspect-[4/3]"} object-cover img-warm scale-110`}
            data-testid={`chapter-image-${chapter.num}`}
          />
        </div>
        <figcaption className="mt-3 font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-muted-foreground">
          {chapter.caption}
        </figcaption>
      </motion.figure>
    </div>
  );
}

export default function Chapters() {
  return (
    <section id="the-van" className="container mx-auto px-6 md:px-12 pt-24 md:pt-36" data-testid="chapters-section">
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4"
      >
        A manifesto in three parts
      </motion.p>
      {CHAPTERS.map((c, i) => (
        <Chapter key={c.num} chapter={c} flip={i % 2 === 1} />
      ))}
    </section>
  );
}
